/**
 * The client's copy of the world.
 *
 * Two different jobs live here and they work differently on purpose.
 *
 * The local player is *predicted*: input is simulated immediately using the same
 * code the server runs, so pressing a key moves the character on the same frame
 * rather than a round trip later.
 *
 * Everything else is *interpolated*: other players and NPCs are drawn slightly in
 * the past, between the last two positions the server sent, which turns ten updates
 * a second into smooth motion.
 */

import {
  CHUNK_TILES,
  CollisionFlag,
  CollisionMap,
  MAX_PLANE,
  createMovementState,
  currentSpeed,
  findPath,
  setPath,
  SightMap,
  MOVE_STEP_MS,
  restoreRunEnergy,
  SKILL_COUNT,
  stepMovement,
  tileOf,
  wallFlagFor,
  type MovementState,
  type WorldPos,
  layerOf,
  type ObjectLayer,
} from '@inverse/common';
import { DEFAULT_LOOK, PatchKind, type WorldPatchPacket } from '@inverse/protocol';
import type {
  EntityLook,
  EntityPosition,
  EntitySpawnPacket,
  MoveCorrectionPacket,
  GroundGonePacket,
  GroundPacket,
  PlacedObject,
  PlayerStatePacket,
  RegionPacket,
} from '@inverse/protocol';

/**
 * A region as the client holds it: what the server said, plus the ground so far.
 *
 * The arrays are the size of the whole place and start out empty, because
 * everything that reads them works in the region's own tiles and should not have
 * to care which squares have turned up. What has not arrived reads as solid,
 * which is the safe direction: prediction refuses to walk into it rather than
 * running out over nothing and being snapped back.
 */
export interface LocalRegion extends RegionPacket {
  heights: Float32Array;
  flags: Uint8Array;
  upperFlags: Uint8Array[];
  ground: Uint8Array;
  water: Float32Array;
  /**
   * Which way the water runs, two entries a tile, in hundredths of a tile a
   * second east and north.
   *
   * Bytes rather than floats because it is read once when a piece of water is
   * built and never again, and a continent is two and a half million tiles: the
   * same thing in floats would be twenty megabytes to say that almost all of it
   * lies still.
   */
  flow: Int8Array;
  /** Which named area each tile is in, one past the start of the chart's areas. */
  area: Uint8Array;
  objects: PlacedObject[];
  /** Which squares have arrived, keyed by where each one starts. */
  readonly squares: Set<string>;
}

/**
 * How far behind the present other entities are drawn. Slightly more than the 100ms
 * broadcast interval, so there is always a later sample to interpolate toward even
 * when one packet arrives late.
 */
const INTERPOLATION_DELAY_MS = 130;

/** Samples kept per entity. A second of history is ample. */
const MAX_SAMPLES = 12;

/**
 * Positional disagreement with the server that is tolerated before correcting.
 *
 * Both sides run identical movement code, so ordinary disagreement is only the
 * client being ahead in time by roughly half the round trip. Snapping to the
 * server's answer every hundred milliseconds would drag the player backward
 * constantly. Beyond this threshold something is genuinely wrong, and being right
 * matters more than being smooth.
 */
const CORRECTION_SNAP_TILES = 1.5;

/**
 * How far the two sides may differ before it is treated as disagreement, on top of
 * whatever the trip time explains.
 */
const CORRECTION_TOLERANCE = 0.2;

/** How far off the walked line the server may be and still be walking the same one. */
const ROUTE_TOLERANCE = 0.4;

/**
 * How long after finishing a route the server is still allowed to be finishing it.
 *
 * Generous, because it only applies to a server that is on the same line: a full
 * round trip plus the tick it waited to act on the click, which is what the little
 * bounce at the end of a walk was made of.
 */
const ARRIVAL_GRACE_MS = 1200;

/** How fast a correction is walked off, in tiles per second, plus a share of what is left. */
const CATCH_UP_SPEED = 1.2;
const CATCH_UP_FRACTION = 2.5;

export interface EntitySample {
  time: number;
  x: number;
  z: number;
  heading: number;
}

export class RemoteEntity {
  readonly id: number;
  readonly kind: number;
  readonly defId: number;
  readonly name: string;
  readonly combatLevel: number;
  /** Item id per equipment slot, zero for an empty one. Kept for Examine. */
  equipment: number[];
  /** The model and colours to draw this one with. */
  look: EntityLook;

  private readonly samples: EntitySample[] = [];

  /** Where the entity is drawn this frame. */
  x: number;
  z: number;
  plane: number;
  heading: number;
  moving = false;
  running = false;

  constructor(packet: EntitySpawnPacket) {
    this.id = packet.entityId;
    this.kind = packet.kind;
    this.defId = packet.defId;
    this.name = packet.name;
    this.combatLevel = packet.combatLevel;
    this.equipment = packet.equipment;
    this.look = packet.look;
    this.x = packet.pos.x;
    this.z = packet.pos.z;
    this.plane = packet.pos.plane;
    this.heading = packet.heading;
    this.samples.push({ time: performance.now(), x: this.x, z: this.z, heading: this.heading });
  }

  push(position: EntityPosition, now: number): void {
    this.plane = position.plane;
    this.moving = position.moving;
    this.running = position.running;
    this.samples.push({
      time: now,
      x: position.x,
      z: position.z,
      heading: position.heading,
    });
    if (this.samples.length > MAX_SAMPLES) this.samples.shift();
  }

  /** Advances the drawn position to the interpolated point for this frame. */
  interpolate(now: number): void {
    const target = now - INTERPOLATION_DELAY_MS;

    if (this.samples.length === 0) return;

    if (this.samples.length === 1) {
      const only = this.samples[0];
      if (only) {
        this.x = only.x;
        this.z = only.z;
        this.heading = only.heading;
      }
      return;
    }

    // Find the pair of samples that straddle the render time.
    let before = this.samples[0];
    let after = this.samples[this.samples.length - 1];

    for (let i = 0; i < this.samples.length - 1; i++) {
      const current = this.samples[i];
      const next = this.samples[i + 1];
      if (!current || !next) continue;
      if (current.time <= target && next.time >= target) {
        before = current;
        after = next;
        break;
      }
    }

    if (!before || !after) return;

    if (after.time <= before.time) {
      this.x = after.x;
      this.z = after.z;
      this.heading = after.heading;
      return;
    }

    const t = Math.min(Math.max((target - before.time) / (after.time - before.time), 0), 1);
    this.x = before.x + (after.x - before.x) * t;
    this.z = before.z + (after.z - before.z) * t;
    this.heading = shortestAngle(before.heading, after.heading, t);
  }
}

/** Interpolates an angle the short way round, so facing never spins through north. */
function shortestAngle(from: number, to: number, t: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return from + delta * t;
}

export interface PendingInput {
  sequence: number;
  running: boolean;
}

export class WorldState {
  region: LocalRegion | null = null;
  collision: CollisionMap | null = null;

  /**
   * What stops a shot, which the client needs only to predict where a fight at a
   * distance is walked to. The server's copy is the one that decides.
   */
  sight: SightMap | null = null;

  /** The scenery that stops a shot: walls, not fences. */
  private readonly sightBlockers = new Set<number>();

  readonly entities = new Map<number, RemoteEntity>();

  /** Entity id of the local player. */
  localId = 0;
  localName = '';
  /** What the character being played looks like. */
  localLook: EntityLook = { ...DEFAULT_LOOK };
  combatLevel = 3;

  /** Predicted movement state for the local player. */
  readonly local: MovementState = createMovementState({ x: 0, z: 0, plane: 0 });

  experience = new Float64Array(SKILL_COUNT);
  levels = new Uint8Array(SKILL_COUNT);

  /** Input frames sent but not yet acknowledged, replayed after a correction. */
  private pending: PendingInput[] = [];

  /** When the last route ran out, or null if none has. See applyCorrection. */
  private arrivedAt: number | null = null;
  private sequence = 0;

  /** Visual offset that decays to zero, so a correction is absorbed rather than seen. */
  private smoothX = 0;
  private smoothZ = 0;

  /**
   * Where the local player was before the most recent movement step.
   *
   * Prediction runs on the 20Hz clock, but the screen redraws three or four times as
   * often. Drawing the stepped position directly means the same position three frames
   * running and then a jump, which reads as a stutter that other players do not have,
   * because they are interpolated. Rendering between this and the current position
   * puts the local player on the same footing.
   */
  private previousX = 0;
  private previousZ = 0;

  agilityLevel = 1;
  ready = false;

  /**
   * Round trip time, from the connection.
   *
   * Used to judge corrections: the server is always a trip behind, so a gap of
   * about that much is agreement rather than error.
   */
  latency = 0;

  /**
   * Where the local player was after each simulated step, by input sequence.
   *
   * A correction says where the server had the player at one sequence. Comparing
   * that against where this client had them at the same sequence is the only
   * comparison that means anything; comparing it against where they are now
   * measures the trip time and calls it an error.
   */
  private readonly history: { sequence: number; x: number; z: number }[] = [];

  /**
   * Somewhere new. Makes room for the ground rather than receiving it.
   *
   * The ground arrives a square at a time from here on, so what this does is
   * work out how large a place it is and allocate the arrays that will be
   * filled in. Everything reads those arrays exactly as it did when they came
   * whole; the only difference is that parts of them have not arrived yet, and
   * `hasGround` is what says which.
   */
  applyRegion(packet: RegionPacket): void {
    const tiles = packet.width * packet.height;

    this.region = {
      ...packet,
      heights: new Float32Array((packet.width + 1) * (packet.height + 1)),
      // Solid until told otherwise. Ground nobody has been sent yet is ground
      // nobody should be predicted into: the alternative is running out over
      // open air for half a second and being snapped back.
      flags: new Uint8Array(tiles).fill(CollisionFlag.Blocked),
      upperFlags: Array.from({ length: MAX_PLANE }, () =>
        new Uint8Array(tiles).fill(CollisionFlag.Blocked),
      ),
      ground: new Uint8Array(tiles),
      water: new Float32Array(tiles),
      flow: new Int8Array(tiles * 2),
      area: new Uint8Array(tiles),
      objects: [],
      squares: new Set<string>(),
    };

    this.collision = new CollisionMap(packet.width, packet.height, packet.originX, packet.originZ);
    this.sight = new SightMap(packet.width, packet.height, packet.originX, packet.originZ);
    for (let plane = 0; plane <= MAX_PLANE; plane++) {
      for (let tz = 0; tz < packet.height; tz++) {
        for (let tx = 0; tx < packet.width; tx++) {
          this.collision.setFlags(
            packet.originX + tx,
            packet.originZ + tz,
            plane,
            CollisionFlag.Blocked,
          );
        }
      }
    }

    this.ground = new Uint8Array(tiles);
  }

  /**
   * One square of ground, written into the arrays everything else reads.
   *
   * A square carries the corners along its own edges, and the squares either
   * side of a join both carry the ones between them. Writing the same number
   * twice costs nothing and means a square can arrive on its own.
   */
  applyGround(packet: GroundPacket): void {
    const region = this.region;
    if (!region || region.id !== packet.region) return;

    const heightStride = region.width + 1;
    for (let z = 0; z <= packet.height; z++) {
      for (let x = 0; x <= packet.width; x++) {
        const into = (packet.tz + z) * heightStride + packet.tx + x;
        if (into < region.heights.length) {
          region.heights[into] = packet.heights[z * (packet.width + 1) + x] ?? 0;
        }
      }
    }

    for (let z = 0; z < packet.height; z++) {
      for (let x = 0; x < packet.width; x++) {
        const into = (packet.tz + z) * region.width + packet.tx + x;
        const from = z * packet.width + x;

        region.flags[into] = packet.flags[from] ?? 0;
        region.ground[into] = packet.ground[from] ?? 0;
        region.water[into] = packet.water[from] ?? 0;
        region.area[into] = packet.area[from] ?? 0;
        // Cleared before the square's own currents are laid in, or water that
        // has been drained goes on running where it used to.
        region.flow[into * 2] = 0;
        region.flow[into * 2 + 1] = 0;

        packet.upperFlags.forEach((held, above) => {
          const plane = region.upperFlags[above];
          if (plane) plane[into] = held[from] ?? 0;
        });
      }
    }

    for (const one of packet.flow) {
      const x = one.at % packet.width;
      const z = (one.at - x) / packet.width;
      const into = (packet.tz + z) * region.width + packet.tx + x;
      if (into < 0 || into >= region.width * region.height) continue;

      region.flow[into * 2] = Math.round(one.x * 100);
      region.flow[into * 2 + 1] = Math.round(one.z * 100);
    }

    // Anything that was standing here before is replaced rather than added to,
    // so a square arriving twice does not double its scenery.
    const inside = (one: { tx: number; tz: number }): boolean =>
      one.tx >= packet.tx &&
      one.tx < packet.tx + packet.width &&
      one.tz >= packet.tz &&
      one.tz < packet.tz + packet.height;

    region.objects = region.objects.filter((one) => !inside(one)).concat(packet.objects);
    region.squares.add(`${packet.tx}.${packet.tz}`);

    this.rebuildCollision(packet.tx, packet.tz, packet.width, packet.height);
  }

  /** A square somebody has walked far enough away from to forget. */
  forgetGround(packet: GroundGonePacket): void {
    const region = this.region;
    if (!region || region.id !== packet.region) return;

    for (const square of packet.squares) {
      const width = Math.min(CHUNK_TILES, region.width - square.tx);
      const height = Math.min(CHUNK_TILES, region.height - square.tz);
      if (width <= 0 || height <= 0) continue;

      for (let z = 0; z < height; z++) {
        for (let x = 0; x < width; x++) {
          const at = (square.tz + z) * region.width + square.tx + x;
          region.flags[at] = CollisionFlag.Blocked;
          region.ground[at] = 0;
          region.water[at] = 0;
          region.flow[at * 2] = 0;
          region.flow[at * 2 + 1] = 0;
          region.area[at] = 0;
          for (const plane of region.upperFlags) plane[at] = CollisionFlag.Blocked;
        }
      }

      region.objects = region.objects.filter(
        (one) =>
          one.tx < square.tx ||
          one.tx >= square.tx + width ||
          one.tz < square.tz ||
          one.tz >= square.tz + height,
      );
      region.squares.delete(`${square.tx}.${square.tz}`);

      this.rebuildCollision(square.tx, square.tz, width, height);
    }
  }

  /** Whether the ground under a tile has arrived, in the region's own tiles. */
  hasGround(tx: number, tz: number): boolean {
    const region = this.region;
    if (!region) return false;

    const cx = Math.floor(tx / CHUNK_TILES) * CHUNK_TILES;
    const cz = Math.floor(tz / CHUNK_TILES) * CHUNK_TILES;
    return region.squares.has(`${cx}.${cz}`);
  }

  /**
   * Puts one square of the collision map back together.
   *
   * The server sends the map it simulates against, which is the ground and
   * everything standing on it already mixed together. Taking a wall down means
   * knowing what the ground under it was, so the two are separated here and
   * kept apart from now on.
   */
  private rebuildCollision(tx: number, tz: number, width: number, height: number): void {
    const region = this.region;
    const map = this.collision;
    if (!region || !map) return;

    for (let z = 0; z < height; z++) {
      for (let x = 0; x < width; x++) {
        const at = (tz + z) * region.width + tx + x;
        const worldX = region.originX + tx + x;
        const worldZ = region.originZ + tz + z;

        map.setFlags(worldX, worldZ, 0, region.flags[at] ?? 0);
        region.upperFlags.forEach((held, above) => {
          map.setFlags(worldX, worldZ, above + 1, held[at] ?? 0);
        });

        this.ground[at] = (region.flags[at] ?? 0) & ~this.objectFlagsAt(tx + x, tz + z);
      }
    }

    this.refreshSight(tx, tz, width, height);
  }

  /** The ground itself, without anything standing on it. */
  private ground = new Uint8Array(0);

  /**
   * What everything on a tile blocks.
   *
   * Footprints wider than a tile are the server's business: it sends the map it
   * built, and a patch for every tile that changes, so the client never has to
   * work out where the rest of a large object lands.
   */
  private objectFlagsAt(localX: number, localZ: number, plane = 0): number {
    const region = this.region;
    if (!region) return CollisionFlag.None;

    let flags = CollisionFlag.None;
    for (const object of region.objects) {
      if (object.tx !== localX || object.tz !== localZ) continue;
      if ((object.plane ?? 0) !== plane) continue;

      const barrier = this.barrierOf(object.id);
      if (barrier === 'solid') flags |= CollisionFlag.Blocked;
      else if (barrier === 'wall') flags |= wallFlagFor(object.rotation);
    }

    return flags;
  }

  /** Whether a floor has been laid on a tile, which is what makes it walkable upstairs. */
  private hasFloorAt(localX: number, localZ: number, plane: number): boolean {
    const region = this.region;
    if (!region) return false;

    return region.objects.some(
      (object) =>
        object.tx === localX &&
        object.tz === localZ &&
        (object.plane ?? 0) === plane &&
        this.layerFor(object.id) === 'floor',
    );
  }

  /**
   * Applies a change to the world somebody made while playing.
   *
   * Collision is updated here as well as in the scene, so the wall that just
   * appeared is a wall to this client's prediction rather than something it walks
   * through and is pulled back out of.
   */
  applyPatch(packet: WorldPatchPacket): void {
    const region = this.region;
    if (!region || !this.collision) return;

    const localX = packet.tx - region.originX;
    const localZ = packet.tz - region.originZ;

    switch (packet.kind) {
      case PatchKind.ObjectAdded: {
        const layer = this.layerFor(packet.value);

        // A tile carries one of each: something laid on it, something standing on
        // it, something over it, and a wall on each side. What arrives displaces
        // what was in the same layer and leaves the rest, which is how a chest
        // put down on boards keeps the boards. The same rule the server follows.
        region.objects = region.objects.filter((object) => {
          if (object.tx !== localX || object.tz !== localZ) return true;
          // Each floor of a building keeps its own layers: a chest upstairs does
          // not displace the chest below it.
          if ((object.plane ?? 0) !== packet.plane) return true;
          if (this.layerFor(object.id) !== layer) return true;

          return layer === 'wall' ? object.rotation !== packet.rotation : false;
        });

        region.objects.push({
          id: packet.value,
          tx: localX,
          tz: localZ,
          plane: packet.plane,
          rotation: packet.rotation,
          // Drawing size travels with the placement, or anything scattered at a
          // size of its own would snap back to the usual one the moment it
          // arrived through a patch rather than with the region.
          scale: packet.scale ?? 1,
          // And so does a height of its own, or a dock would drop onto the water
          // the moment it was laid and rise back up on the next login.
          ...(packet.lift !== undefined ? { lift: packet.lift } : {}),
          // And a shape of its own, or a pine would turn back into whatever its
          // kind is drawn as the moment anything changed on its tile.
          ...(packet.looks !== undefined ? { looks: packet.looks } : {}),
        });
        break;
      }

      case PatchKind.ObjectRemoved:
        region.objects = region.objects.filter((object) => {
          if (object.tx !== localX || object.tz !== localZ) return true;
          if ((object.plane ?? 0) !== packet.plane) return true;
          // Older patches name no piece, and meant whatever was standing there.
          if (packet.value === 0) return this.barrierOf(object.id) === 'wall';
          return object.id !== packet.value || object.rotation !== packet.rotation;
        });
        break;

      case PatchKind.WaysChanged: {
        // What one particular thing can be worked for. Kept on the object
        // standing there, so the menu on it offers what that one holds rather
        // than everything its kind knows how to do.
        for (const object of region.objects) {
          if (object.tx !== localX || object.tz !== localZ) continue;
          if ((object.plane ?? 0) !== packet.plane || object.id !== packet.value) continue;

          object.ways = (packet.ways ?? []).map((action) => ({ action, catches: [] }));
        }
        break;
      }

      case PatchKind.TileChanged: {
        // What arrives is the whole tile, so what belongs to the ground is what
        // is left once the things standing on it are taken out.
        const index = localZ * region.width + localX;
        if (region.ground) region.ground[index] = packet.ground ?? 0;
        if (region.water && packet.water !== undefined) region.water[index] = packet.water;
        // A border that was redrawn in the tools, so the name in the corner
        // changes under somebody standing on the tile rather than on reconnect.
        if (region.area && packet.area !== undefined) region.area[index] = packet.area;
        this.ground[index] = packet.value & ~this.objectFlagsAt(localX, localZ);
        this.refreshTile(packet.tx, packet.tz);
        return;
      }
    }

    this.refreshTile(packet.tx, packet.tz, packet.plane);
  }

  /** How a piece of scenery stops movement, from the definitions sent at login. */
  barrierOf(objectId: number): 'solid' | 'wall' | 'open' {
    return this.barriers.get(objectId) ?? 'solid';
  }

  /** What the tools said each piece of scenery blocks. Set from the definitions. */
  private readonly barriers = new Map<number, 'solid' | 'wall' | 'open'>();

  setBarriers(
    objects: readonly {
      id: number;
      barrier: 'solid' | 'wall' | 'open';
      floor?: boolean;
      roof?: boolean;
      layer?: ObjectLayer;
      blocksSight?: boolean;
    }[],
  ): void {
    this.barriers.clear();
    this.layers.clear();
    this.sightBlockers.clear();

    for (const object of objects) {
      this.barriers.set(object.id, object.barrier);
      if (object.blocksSight) this.sightBlockers.add(object.id);
      this.layers.set(
        object.id,
        layerOf({
          width: 1,
          height: 1,
          solid: object.barrier === 'solid',
          barrier: object.barrier,
          floor: object.floor,
          roof: object.roof,
          layer: object.layer,
        }),
      );
    }

    // Which things stop a shot may have changed under scenery already standing.
    if (this.region) this.refreshSight(0, 0, this.region.width, this.region.height);
  }

  /** Which layer of a tile each kind of scenery belongs to. */
  private readonly layers = new Map<number, ObjectLayer>();

  /**
   * Works out again which tiles of a stretch stop a shot, in the region's own tiles.
   *
   * Ground floor only, as on the server: a wall upstairs is not in the way of
   * somebody shooting across the yard below it.
   */
  private refreshSight(tx: number, tz: number, width: number, height: number): void {
    const region = this.region;
    const sight = this.sight;
    if (!region || !sight) return;

    for (let z = tz; z < tz + height; z++) {
      for (let x = tx; x < tx + width; x++)
        sight.set(region.originX + x, region.originZ + z, false);
    }

    if (this.sightBlockers.size === 0) return;
    for (const object of region.objects) {
      if ((object.plane ?? 0) !== 0) continue;
      if (object.tx < tx || object.tz < tz || object.tx >= tx + width || object.tz >= tz + height) {
        continue;
      }
      if (!this.sightBlockers.has(object.id)) continue;
      sight.set(region.originX + object.tx, region.originZ + object.tz, true);
    }
  }

  layerFor(objectId: number): ObjectLayer {
    return this.layers.get(objectId) ?? 'standing';
  }

  /**
   * Works a tile's collision out again from what is standing on it.
   *
   * The server is the authority, but prediction has to agree with it in the same
   * frame the change lands, or walking into a wall that has just gone up feels
   * like a stutter rather than a wall.
   */
  private refreshTile(tx: number, tz: number, plane = 0): void {
    const region = this.region;
    if (!region || !this.collision) return;

    const localX = tx - region.originX;
    const localZ = tz - region.originZ;

    if (plane > 0) {
      // Upstairs there is nothing to stand on except what was built, so a tile is
      // blocked unless a floor was laid on it, and then blocked again by whatever
      // stands on that floor.
      const built = this.hasFloorAt(localX, localZ, plane)
        ? this.objectFlagsAt(localX, localZ, plane)
        : CollisionFlag.Blocked;

      this.collision.setFlags(tx, tz, plane, built);
      return;
    }

    const index = localZ * region.width + localX;
    let flags = (this.ground[index] ?? 0) | this.objectFlagsAt(localX, localZ);

    // Boards over water make a bridge. The water stays, and stays drawn; what
    // goes is the part of it that stopped anybody crossing. Water blocks on its
    // own, so the boards have to say they are there rather than only unsetting a
    // flag the water never needed.
    if ((flags & CollisionFlag.Water) !== 0 && this.hasFloorAt(localX, localZ, 0)) {
      flags = (flags & ~CollisionFlag.Blocked) | CollisionFlag.Decked;
    }

    region.flags[index] = flags;
    this.collision.setFlags(tx, tz, 0, flags);
    this.refreshSight(localX, localZ, 1, 1);
  }

  applyPlayerState(packet: PlayerStatePacket): void {
    this.localName = packet.name;
    this.localLook = packet.look;
    this.combatLevel = packet.combatLevel;
    this.local.pos = { ...packet.pos };
    this.local.runEnergy = packet.runEnergy;
    this.local.running = packet.running;
    setPath(this.local, []);
    this.previousX = packet.pos.x;
    this.previousZ = packet.pos.z;

    for (let skill = 0; skill < SKILL_COUNT; skill++) {
      this.experience[skill] = packet.experience[skill] ?? 0;
      this.levels[skill] = packet.levels[skill] ?? 1;
    }

    this.ready = true;
  }

  spawn(packet: EntitySpawnPacket): void {
    if (packet.entityId === this.localId) return;
    this.entities.set(packet.entityId, new RemoteEntity(packet));
  }

  despawn(ids: readonly number[]): void {
    for (const id of ids) this.entities.delete(id);
  }

  positions(updates: readonly EntityPosition[]): void {
    const now = performance.now();
    for (const update of updates) {
      this.entities.get(update.entityId)?.push(update, now);
    }
  }

  /**
   * Reconciles the predicted position with the server's.
   *
   * Both sides walk the same route from the same search, so nearly every difference
   * between them is the server being a little way behind on that route, which is
   * latency rather than error. What is left, a teleport or a route the server
   * refused, is taken as the truth and walked off rather than jumped.
   */
  applyCorrection(packet: MoveCorrectionPacket): void {
    if (!this.collision) return;

    this.local.runEnergy = packet.runEnergy;
    this.pending = this.pending.filter((frame) => frame.sequence > packet.sequence);

    const mine = this.historyAt(packet.sequence);

    // While walking a route, the server is behind on the same line rather than
    // somewhere else: it started the route when the click reached it, a trip later
    // than this client did. How far behind does not matter so long as it is on the
    // line. Closing that gap ten times a second is what rubber banding is.
    //
    // The same holds for a moment after arriving. This client finishes the walk
    // first and stands on the destination while the server is still walking the
    // last stretch of it, and correcting to that is the little bounce at the end
    // of every route.
    const arriving =
      this.arrivedAt !== null && performance.now() - this.arrivedAt < ARRIVAL_GRACE_MS;
    if ((this.local.path.length > 0 || arriving) && this.onRoute(packet.pos)) return;

    // What this client had at the same moment the server is describing.
    if (mine) {
      const disagreement = Math.hypot(packet.pos.x - mine.x, packet.pos.z - mine.z);
      if (disagreement <= this.agreementTolerance()) return;
    }

    // Beyond that the two are somewhere genuinely different, and the server is the
    // one that decides. Carry the difference as a decaying visual offset so the
    // model walks the gap rather than jumping it.
    this.smoothX += this.local.pos.x - packet.pos.x;
    this.smoothZ += this.local.pos.z - packet.pos.z;

    if (Math.hypot(this.smoothX, this.smoothZ) > CORRECTION_SNAP_TILES) {
      // Too far to walk off. Show the jump rather than sliding across the world.
      this.smoothX = 0;
      this.smoothZ = 0;
    }

    this.local.pos = { ...packet.pos };
    this.previousX = packet.pos.x;
    this.previousZ = packet.pos.z;
    this.history.length = 0;

    // Walking somewhere is a route, and the route was worked out from a position
    // that has just been corrected. Search again from where the player actually is,
    // or the rest of the walk fights the correction all the way there.
    this.repath();
  }

  /** Whether a position sits on the line this client is currently walking. */
  private onRoute(pos: WorldPos): boolean {
    if (this.routeLine.length < 2) return false;

    for (let i = 1; i < this.routeLine.length; i++) {
      const from = this.routeLine[i - 1];
      const to = this.routeLine[i];
      if (!from || !to) continue;
      if (distanceToSegment(pos, from, to) <= ROUTE_TOLERANCE) return true;
    }

    return false;
  }

  /** The predicted position at an acknowledged sequence, if it is still remembered. */
  private historyAt(sequence: number): { x: number; z: number } | null {
    let found: { x: number; z: number } | null = null;

    while (this.history.length > 0) {
      const entry = this.history[0];
      if (!entry || entry.sequence > sequence) break;
      this.history.shift();
      if (entry.sequence === sequence) found = { x: entry.x, z: entry.z };
    }

    return found;
  }

  /**
   * How far apart the two sides may be before it counts as disagreement.
   *
   * The server is describing a moment that is half a round trip old, and while
   * walking it also started the route that much later, so a gap of roughly the
   * distance covered in that time is expected and correcting it would produce the
   * rubber banding it is meant to prevent.
   */
  private agreementTolerance(): number {
    const seconds = Math.min(this.latency, 400) / 1000;
    return CORRECTION_TOLERANCE + currentSpeed(this.local) * seconds;
  }

  /** Re-runs the route from where the player is now, keeping the destination. */
  private repath(): void {
    const destination = this.route[this.route.length - 1];
    if (!destination || this.local.path.length === 0) return;
    this.pathTo(destination.tx, destination.tz, this.local.pos.plane);
  }

  /** Runs one movement step locally and records the frame for replay. */
  simulate(running: boolean): PendingInput | null {
    if (!this.collision || !this.ready) return null;

    this.sequence++;
    const frame: PendingInput = { sequence: this.sequence, running };

    this.local.running = running;

    this.previousX = this.local.pos.x;
    this.previousZ = this.local.pos.z;

    const walking = this.local.path.length > 0;
    const moved = stepMovement(this.collision, this.local, MOVE_STEP_MS / 1000);

    // The moment a route runs out is worth remembering: for a short while after it,
    // a server still finishing the same route is agreement rather than error.
    if (walking && this.local.path.length === 0) this.arrivedAt = performance.now();
    // Matches the server exactly: energy returns while standing still, run or walk.
    if (!moved) restoreRunEnergy(this.local, this.agilityLevel, MOVE_STEP_MS / 1000);

    this.pending.push(frame);
    // Anything this old will never be acknowledged; the connection has bigger problems.
    if (this.pending.length > 60) this.pending.shift();

    this.history.push({ sequence: frame.sequence, x: this.local.pos.x, z: this.local.pos.z });
    if (this.history.length > 120) this.history.shift();

    return frame;
  }

  /**
   * Predicts a click-to-move path locally, using the same search the server runs.
   *
   * The route is kept so it can be sent with the destination. Two searches from
   * two positions can find two different ways around the same corner, and the
   * player feels that as being pulled sideways, so only one search is trusted.
   */
  pathTo(tx: number, tz: number, plane: number): boolean {
    if (!this.collision) return false;

    // Whatever comes of this, the last route is spent. Leaving it lying about is
    // what made clicking the tile somebody is already standing on walk them back
    // to where they came from and return: there was no new path, so the old one
    // went to the server and the server believed it.
    this.route = [];
    this.routeLine = [];

    if (!this.collision.inBounds(tx, tz)) return false;

    const from = tileOf(this.local.pos);
    const path = findPath(this.collision, from, { tx, tz, plane });
    if (path.points.length === 0) return false;

    setPath(this.local, path.points);
    this.route = path.points.map((point) => ({
      tx: Math.floor(point.x),
      tz: Math.floor(point.z),
    }));
    this.routeLine = [{ ...this.local.pos }, ...path.points.map((point) => ({ ...point }))];
    return true;
  }

  /**
   * Walks a route worked out elsewhere, kept exactly as given.
   *
   * For a walk that stops short of where a search would end: a route to a target
   * cut off where a shot at it comes on. Searching again to that tile could find
   * a different way there, and the server walks this one.
   */
  walkRoute(points: WorldPos[]): boolean {
    this.route = [];
    this.routeLine = [];
    if (points.length === 0) return false;

    setPath(this.local, points);
    this.route = points.map((point) => ({ tx: Math.floor(point.x), tz: Math.floor(point.z) }));
    this.routeLine = [{ ...this.local.pos }, ...points.map((point) => ({ ...point }))];
    return true;
  }

  /** The route the last prediction produced, as tiles. */
  route: { tx: number; tz: number }[] = [];

  /**
   * The whole of that route in world coordinates, including where it started.
   *
   * Kept after the waypoints have been walked off, because judging a correction
   * means asking whether the server is somewhere on the line this client is
   * walking. It nearly always is, a little way behind, and that is agreement
   * rather than error.
   */
  private routeLine: WorldPos[] = [];

  /**
   * Walks the correction offset off, a little each frame.
   *
   * At a bounded speed rather than exponentially. An offset that vanishes in a
   * quarter of a second is a slide fast enough to read as a jump, which is the
   * thing the offset exists to avoid; taken at something near walking pace it
   * disappears into the stride instead. The proportional part is what keeps a
   * large correction from taking all day.
   */
  decaySmoothing(deltaMs: number): void {
    const carried = Math.hypot(this.smoothX, this.smoothZ);
    if (carried === 0) return;

    const seconds = deltaMs / 1000;
    const closed = Math.min(
      carried,
      CATCH_UP_SPEED * seconds + carried * CATCH_UP_FRACTION * seconds,
    );
    const left = (carried - closed) / carried;

    this.smoothX *= left;
    this.smoothZ *= left;

    if (Math.abs(this.smoothX) < 1e-4) this.smoothX = 0;
    if (Math.abs(this.smoothZ) < 1e-4) this.smoothZ = 0;
  }

  /**
   * Where the local player is drawn.
   *
   * `alpha` is how far the current render frame sits between the last movement step
   * and the next one, which is what turns 20 predicted positions a second into motion
   * that matches the refresh rate. It defaults to 1, the simulated position, which is
   * what anything asking a gameplay question wants.
   */
  renderPosition(alpha = 1): WorldPos {
    const t = Math.min(Math.max(alpha, 0), 1);
    return {
      x: this.previousX + (this.local.pos.x - this.previousX) * t + this.smoothX,
      z: this.previousZ + (this.local.pos.z - this.previousZ) * t + this.smoothZ,
      plane: this.local.pos.plane,
    };
  }

  interpolateAll(now: number): void {
    for (const entity of this.entities.values()) entity.interpolate(now);
  }
}

/** Shortest distance from a point to a line segment, on the ground plane. */
function distanceToSegment(point: WorldPos, from: WorldPos, to: WorldPos): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const lengthSquared = dx * dx + dz * dz;

  if (lengthSquared === 0) return Math.hypot(point.x - from.x, point.z - from.z);

  const along = ((point.x - from.x) * dx + (point.z - from.z) * dz) / lengthSquared;
  const clamped = Math.min(Math.max(along, 0), 1);
  return Math.hypot(point.x - (from.x + dx * clamped), point.z - (from.z + dz * clamped));
}
